import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faUser,
    faClock,
    faFileInvoiceDollar,
    faArrowRight,
    faChevronDown,
    faChevronUp,
    faSpinner,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { getDateAsReadableFormat } from '../../helper/general';
import ApiRoute from '../../helper/api';

function LogCustomerSidebar({ isOpen, onClose, selectedCustomer }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [expandedLogs, setExpandedLogs] = useState({});
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
        from: 0,
        to: 0
    });

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            setTimeout(() => setIsAnimating(true), 10);
            // بارگذاری لاگ‌ها
            if (selectedCustomer?._id) {
                loadLogs(selectedCustomer._id);
            }
        } else {
            setIsAnimating(false);
            setTimeout(() => {
                setIsVisible(false);
                // reset state ها
                setLogs([]);
                setExpandedLogs({});
                setIsLoading(false);
                setPagination({
                    current_page: 1,
                    last_page: 1,
                    total: 0,
                    per_page: 10,
                    from: 0,
                    to: 0
                });
            }, 300);
        }
    }, [isOpen, selectedCustomer]);

    const loadLogs = async (customerId, page = 1) => {
        setIsLoading(true);
        try {
            // اضافه کردن پارامتر page به query string
            const queryParams = { page };
            const response = await ApiRoute.customer.logs.fn(customerId, queryParams);
            
            if (response?.status_code === 200 && response.results) {
                const { data, ...paginationData } = response.results;
                setLogs(data || []);
                setPagination({
                    current_page: paginationData.current_page || 1,
                    last_page: paginationData.last_page || 1,
                    total: paginationData.total || 0,
                    per_page: paginationData.per_page || 10,
                    from: paginationData.from || 0,
                    to: paginationData.to || 0
                });
            }
        } catch (error) {
            console.error('خطا در بارگذاری لاگ‌ها:', error);
            setLogs([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                total: 0,
                per_page: 10,
                from: 0,
                to: 0
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page && selectedCustomer?._id) {
            loadLogs(selectedCustomer._id, page);
        }
    };

    const toggleLogExpansion = (logId) => {
        setExpandedLogs(prev => ({
            ...prev,
            [logId]: !prev[logId]
        }));
    };


    // تابع برای تشخیص نوع تغییر
    const getActionType = (action) => {
        if (action.includes('address')) return 'address_change';
        if (action.includes('create')) return 'create_action';
        if (action.includes('update')) return 'update_action';
        if (action.includes('delete')) return 'delete_action';
        if (action.includes('status')) return 'status_change';
        if (action.includes('contacts')) return 'contact_change';
        if (action.includes('personal')) return 'personal_change';
        if (action.includes('legal')) return 'legal_change';
        return 'other';
    };

    // تابع برای ترجمه action
    const translateAction = (action) => {
        const translations = {
            'update_customer_general' : 'ویرایش اطلاعات عمومی',
            'create_customer_address': 'ایجاد آدرس جدید',
            'update_customer_address': 'ویرایش آدرس',
            'delete_customer_address': 'حذف آدرس',
            'update_customer_info': 'ویرایش اطلاعات مشتری',
            'update_customer_contacts': 'ویرایش اطلاعات تماس',
            'update_customer_personal': 'ویرایش اطلاعات شخصی',
            'update_customer_legal': 'ویرایش اطلاعات حقوقی',
            'create_customer': 'ایجاد مشتری جدید',
            'update_customer_status': 'تغییر وضعیت مشتری',
            'update_customer_asan_id': 'ویرایش شناسه آسان',
        };
        return translations[action] || action;
    };

    // تابع برای مقایسه و نمایش تغییرات
    const getChanges = (beforeData, afterData) => {
        const changes = [];
        
        // بررسی تغییرات username
        if (beforeData.username !== afterData.username) {
            changes.push({
                field: 'نام کاربری',
                before: beforeData.username || 'تعریف نشده',
                after: afterData.username || 'تعریف نشده',
                type: 'string'
            });
        }

        // بررسی تغییرات اطلاعات تماس
        if (beforeData.contacts && afterData.contacts) {
            const beforeContacts = beforeData.contacts;
            const afterContacts = afterData.contacts;
            
            if (beforeContacts.cellphone !== afterContacts.cellphone) {
                changes.push({
                    field: 'شماره موبایل',
                    before: beforeContacts.cellphone || 'تعریف نشده',
                    after: afterContacts.cellphone || 'تعریف نشده',
                    type: 'contact'
                });
            }
            
            if (beforeContacts.emergency_phone !== afterContacts.emergency_phone) {
                changes.push({
                    field: 'شماره اضطراری',
                    before: beforeContacts.emergency_phone || 'تعریف نشده',
                    after: afterContacts.emergency_phone || 'تعریف نشده',
                    type: 'contact'
                });
            }
            
            if (beforeContacts.century_code !== afterContacts.century_code) {
                changes.push({
                    field: 'کد قرن',
                    before: beforeContacts.century_code || 'تعریف نشده',
                    after: afterContacts.century_code || 'تعریف نشده',
                    type: 'contact'
                });
            }
        }

        // بررسی تغییرات اطلاعات شخصی
        if (beforeData.personal && afterData.personal) {
            const beforePersonal = beforeData.personal;
            const afterPersonal = afterData.personal;
            
            if (beforePersonal.first_name !== afterPersonal.first_name) {
                changes.push({
                    field: 'نام',
                    before: beforePersonal.first_name || 'تعریف نشده',
                    after: afterPersonal.first_name || 'تعریف نشده',
                    type: 'personal'
                });
            }
            
            if (beforePersonal.last_name !== afterPersonal.last_name) {
                changes.push({
                    field: 'نام خانوادگی',
                    before: beforePersonal.last_name || 'تعریف نشده',
                    after: afterPersonal.last_name || 'تعریف نشده',
                    type: 'personal'
                });
            }
            
            if (beforePersonal.national_code !== afterPersonal.national_code) {
                changes.push({
                    field: 'کد ملی',
                    before: beforePersonal.national_code || 'تعریف نشده',
                    after: afterPersonal.national_code || 'تعریف نشده',
                    type: 'personal'
                });
            }
            
            if (beforePersonal.foreigner_code !== afterPersonal.foreigner_code) {
                changes.push({
                    field: 'کد اتباع خارجی',
                    before: beforePersonal.foreigner_code || 'تعریف نشده',
                    after: afterPersonal.foreigner_code || 'تعریف نشده',
                    type: 'personal'
                });
            }
            
            if (beforePersonal.age !== afterPersonal.age) {
                changes.push({
                    field: 'تاریخ تولد',
                    before: beforePersonal.age || 'تعریف نشده',
                    after: afterPersonal.age || 'تعریف نشده',
                    type: 'personal'
                });
            }
        }

        // بررسی تغییرات اطلاعات حقوقی
        if (beforeData.legal !== afterData.legal) {
            changes.push({
                field: 'اطلاعات حقوقی',
                before: beforeData.legal ? 'تعریف شده' : 'تعریف نشده',
                after: afterData.legal ? 'تعریف شده' : 'تعریف نشده',
                type: 'legal'
            });
        }

        // بررسی تغییرات وضعیت اتباع خارجی
        if (beforeData.is_foreigner !== afterData.is_foreigner) {
            changes.push({
                field: 'وضعیت اتباع خارجی',
                before: beforeData.is_foreigner ? 'بله' : 'خیر',
                after: afterData.is_foreigner ? 'بله' : 'خیر',
                type: 'status'
            });
        }

        // بررسی تغییرات شناسه آسان
        if (JSON.stringify(beforeData.asan_id) !== JSON.stringify(afterData.asan_id)) {
            const formatAsanId = (asanId) => {
                if (!asanId || asanId.length === 0) return 'تعریف نشده';
                return asanId.join(', ');
            };
            
            changes.push({
                field: 'شناسه آسان',
                before: formatAsanId(beforeData.asan_id),
                after: formatAsanId(afterData.asan_id),
                type: 'asan_id'
            });
        }

        // بررسی تغییرات آدرس‌ها
        if (beforeData.addresses || afterData.addresses) {
            const beforeAddresses = beforeData.addresses || [];
            const afterAddresses = afterData.addresses || [];
            
            // اگر تعداد آدرس‌ها تغییر کرده
            if (beforeAddresses.length !== afterAddresses.length) {
                changes.push({
                    field: 'تعداد آدرس‌ها',
                    before: beforeAddresses.length.toString(),
                    after: afterAddresses.length.toString(),
                    type: 'address_count'
                });
            }
            
            // بررسی آدرس‌های جدید (ایجاد شده)
            if (afterAddresses.length > beforeAddresses.length) {
                const newAddresses = afterAddresses.slice(beforeAddresses.length);
                newAddresses.forEach((address, index) => {
                    changes.push({
                        field: `آدرس جدید ${index + 1}`,
                        before: 'اضافه نشده',
                        after: `${address.address?.title || 'بدون عنوان'} - ${address.address?.address || 'بدون آدرس'}`,
                        type: 'address_added'
                    });
                });
            }
            
            // بررسی آدرس‌های حذف شده
            if (beforeAddresses.length > afterAddresses.length) {
                const removedCount = beforeAddresses.length - afterAddresses.length;
                changes.push({
                    field: 'آدرس‌های حذف شده',
                    before: `${removedCount} آدرس`,
                    after: 'حذف شده',
                    type: 'address_removed'
                });
            }
            
            // بررسی تغییرات در آدرس‌های موجود
            const minLength = Math.min(beforeAddresses.length, afterAddresses.length);
            for (let i = 0; i < minLength; i++) {
                const beforeAddr = beforeAddresses[i];
                const afterAddr = afterAddresses[i];
                
                // بررسی تغییرات در جزئیات آدرس
                if (beforeAddr.address && afterAddr.address) {
                    const beforeAddrData = beforeAddr.address;
                    const afterAddrData = afterAddr.address;
                    
                    if (beforeAddrData.title !== afterAddrData.title) {
                        changes.push({
                            field: `آدرس ${i + 1} - عنوان`,
                            before: beforeAddrData.title || 'بدون عنوان',
                            after: afterAddrData.title || 'بدون عنوان',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.address !== afterAddrData.address) {
                        changes.push({
                            field: `آدرس ${i + 1} - آدرس`,
                            before: beforeAddrData.address || 'بدون آدرس',
                            after: afterAddrData.address || 'بدون آدرس',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.desc !== afterAddrData.desc) {
                        changes.push({
                            field: `آدرس ${i + 1} - توضیحات`,
                            before: beforeAddrData.desc || 'بدون توضیحات',
                            after: afterAddrData.desc || 'بدون توضیحات',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.floor !== afterAddrData.floor) {
                        changes.push({
                            field: `آدرس ${i + 1} - طبقه`,
                            before: beforeAddrData.floor || 'تعریف نشده',
                            after: afterAddrData.floor || 'تعریف نشده',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.house_name !== afterAddrData.house_name) {
                        changes.push({
                            field: `آدرس ${i + 1} - نام خانه`,
                            before: beforeAddrData.house_name || 'تعریف نشده',
                            after: afterAddrData.house_name || 'تعریف نشده',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.house_number !== afterAddrData.house_number) {
                        changes.push({
                            field: `آدرس ${i + 1} - شماره خانه`,
                            before: beforeAddrData.house_number || 'تعریف نشده',
                            after: afterAddrData.house_number || 'تعریف نشده',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.postcode !== afterAddrData.postcode) {
                        changes.push({
                            field: `آدرس ${i + 1} - کد پستی`,
                            before: beforeAddrData.postcode || 'تعریف نشده',
                            after: afterAddrData.postcode || 'تعریف نشده',
                            type: 'address_detail'
                        });
                    }
                    
                    if (beforeAddrData.unit !== afterAddrData.unit) {
                        changes.push({
                            field: `آدرس ${i + 1} - واحد`,
                            before: beforeAddrData.unit || 'تعریف نشده',
                            after: afterAddrData.unit || 'تعریف نشده',
                            type: 'address_detail'
                        });
                    }
                }
                
                // بررسی تغییرات در مختصات
                if (beforeAddr.coordinates && afterAddr.coordinates) {
                    const beforeCoords = beforeAddr.coordinates;
                    const afterCoords = afterAddr.coordinates;
                    
                    if (beforeCoords.state?.faname !== afterCoords.state?.faname) {
                        changes.push({
                            field: `آدرس ${i + 1} - استان`,
                            before: beforeCoords.state?.faname || 'تعریف نشده',
                            after: afterCoords.state?.faname || 'تعریف نشده',
                            type: 'address_coordinates'
                        });
                    }
                    
                    if (beforeCoords.city?.faname !== afterCoords.city?.faname) {
                        changes.push({
                            field: `آدرس ${i + 1} - شهر`,
                            before: beforeCoords.city?.faname || 'تعریف نشده',
                            after: afterCoords.city?.faname || 'تعریف نشده',
                            type: 'address_coordinates'
                        });
                    }
                    
                    if (beforeCoords.lat !== afterCoords.lat || beforeCoords.lng !== afterCoords.lng) {
                        changes.push({
                            field: `آدرس ${i + 1} - مختصات`,
                            before: beforeCoords.lat && beforeCoords.lng ? 
                                `${beforeCoords.lat.toFixed(6)}, ${beforeCoords.lng.toFixed(6)}` : 'تعریف نشده',
                            after: afterCoords.lat && afterCoords.lng ? 
                                `${afterCoords.lat.toFixed(6)}, ${afterCoords.lng.toFixed(6)}` : 'تعریف نشده',
                            type: 'address_coordinates'
                        });
                    }
                }
                
                // بررسی تغییرات در وضعیت آدرس
                if (beforeAddr.main !== afterAddr.main) {
                    changes.push({
                        field: `آدرس ${i + 1} - آدرس اصلی`,
                        before: beforeAddr.main ? 'بله' : 'خیر',
                        after: afterAddr.main ? 'بله' : 'خیر',
                        type: 'address_status'
                    });
                }
                
                if (beforeAddr.first !== afterAddr.first) {
                    changes.push({
                        field: `آدرس ${i + 1} - اولین آدرس`,
                        before: beforeAddr.first ? 'بله' : 'خیر',
                        after: afterAddr.first ? 'بله' : 'خیر',
                        type: 'address_status'
                    });
                }
                
                if (beforeAddr.active !== afterAddr.active) {
                    changes.push({
                        field: `آدرس ${i + 1} - فعال`,
                        before: beforeAddr.active ? 'بله' : 'خیر',
                        after: afterAddr.active ? 'بله' : 'خیر',
                        type: 'address_status'
                    });
                }
                
                if (beforeAddr.cellphone !== afterAddr.cellphone) {
                    changes.push({
                        field: `آدرس ${i + 1} - شماره موبایل`,
                        before: beforeAddr.cellphone || 'تعریف نشده',
                        after: afterAddr.cellphone || 'تعریف نشده',
                        type: 'address_contact'
                    });
                }
            }
        }

        return changes;
    };

    if (!isVisible) return null;

    return (
        <>
            {/* پس‌زمینه تاریک */}
            <div 
                className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
                    isAnimating ? 'opacity-50' : 'opacity-0'
                }`}
                onClick={onClose}
            ></div>
            
            {/* سایدبار */}
            <div className={`fixed left-0 top-0 h-full w-[45rem] bg-white shadow-xl z-50 transform transition-all duration-300 ease-in-out ${
                isAnimating ? 'translate-x-0' : '-translate-x-full'
            }`}>
                {/* هدر سایدبار */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                            لاگ‌های مشتری
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {selectedCustomer?.username}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
                    </button>
                </div>

                {/* محتوای لاگ‌ها */}
                <div className="h-full overflow-y-auto pb-20">
                    <div className="p-4 space-y-4">
                        {isLoading ? (
                            <div className="text-center py-8">
                                <FontAwesomeIcon 
                                    icon={faSpinner} 
                                    className="text-gray-400 text-2xl mb-3 animate-spin"
                                />
                                <p className="text-gray-500">در حال بارگذاری لاگ‌ها...</p>
                            </div>
                        ) : logs.length > 0 ? (
                            logs.map((log, index) => {
                                const changes = getChanges(log.before_data, log.after_data);
                                const actionType = getActionType(log.action);
                                const isExpanded = expandedLogs[log._id];

                                return (
                                    <div
                                        key={log._id}
                                        className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 transform ${
                                            isAnimating 
                                                ? 'translate-x-0 opacity-100' 
                                                : 'translate-x-4 opacity-0'
                                        }`}
                                        style={{
                                            transitionDelay: isAnimating ? `${index * 50}ms` : '0ms'
                                        }}
                                    >
                                        {/* هدر لاگ */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-3 h-3 rounded-full ${
                                                    actionType === 'address_change' ? 'bg-blue-500' :
                                                    actionType === 'create_action' ? 'bg-green-500' :
                                                    actionType === 'update_action' ? 'bg-yellow-500' :
                                                    actionType === 'delete_action' ? 'bg-red-500' :
                                                    actionType === 'status_change' ? 'bg-purple-500' :
                                                    actionType === 'contact_change' ? 'bg-cyan-500' :
                                                    actionType === 'personal_change' ? 'bg-indigo-500' :
                                                    actionType === 'legal_change' ? 'bg-pink-500' :
                                                    'bg-orange-500'
                                                }`}></div>
                                                <span className="font-semibold text-gray-800 text-sm">
                                                    {translateAction(log.action)}
                                                </span>
                                            </div>
                                            
                                            {changes.length > 0 && (
                                                <button
                                                    onClick={() => toggleLogExpansion(log._id)}
                                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={isExpanded ? faChevronUp : faChevronDown} 
                                                        className="text-xs"
                                                    />
                                                </button>
                                            )}
                                        </div>

                                        {/* خلاصه تغییرات */}
                                        <div className="text-sm text-gray-600 mb-3">
                                            {changes.length > 0 ? (
                                                <span>{changes.length} مورد تغییر انجام شد</span>
                                            ) : (
                                                <span>هیچ تغییری شناسایی نشد</span>
                                            )}
                                        </div>

                                        {/* جزئیات تغییرات */}
                                        {isExpanded && changes.length > 0 && (
                                            <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
                                                {changes.map((change, changeIndex) => (
                                                    <div key={changeIndex} className="flex items-center justify-between text-xs bg-gray-50 rounded p-2">
                                                        <span className="font-medium text-gray-700">
                                                            {change.field}:
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-1 rounded ${
                                                                change.type === 'string' ? 'bg-orange-100 text-orange-700' :
                                                                change.type === 'contact' ? 'bg-cyan-100 text-cyan-700' :
                                                                change.type === 'personal' ? 'bg-indigo-100 text-indigo-700' :
                                                                change.type === 'legal' ? 'bg-pink-100 text-pink-700' :
                                                                change.type === 'status' ? 'bg-purple-100 text-purple-700' :
                                                                change.type === 'asan_id' ? 'bg-violet-100 text-violet-700' :
                                                                change.type === 'address_count' ? 'bg-blue-100 text-blue-700' :
                                                                change.type === 'address_added' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'address_removed' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'address_detail' ? 'bg-amber-100 text-amber-700' :
                                                                change.type === 'address_coordinates' ? 'bg-teal-100 text-teal-700' :
                                                                change.type === 'address_status' ? 'bg-yellow-100 text-yellow-700' :
                                                                change.type === 'address_contact' ? 'bg-emerald-100 text-emerald-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {change.after}
                                                            </span>
                                                            <FontAwesomeIcon icon={faArrowRight} className="text-gray-400" />
                                                            <span className={`px-2 py-1 rounded ${
                                                                change.type === 'string' ? 'bg-orange-100 text-orange-700' :
                                                                change.type === 'contact' ? 'bg-cyan-100 text-cyan-700' :
                                                                change.type === 'personal' ? 'bg-indigo-100 text-indigo-700' :
                                                                change.type === 'legal' ? 'bg-pink-100 text-pink-700' :
                                                                change.type === 'status' ? 'bg-purple-100 text-purple-700' :
                                                                change.type === 'asan_id' ? 'bg-violet-100 text-violet-700' :
                                                                change.type === 'address_count' ? 'bg-blue-100 text-blue-700' :
                                                                change.type === 'address_added' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'address_removed' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'address_detail' ? 'bg-amber-100 text-amber-700' :
                                                                change.type === 'address_coordinates' ? 'bg-teal-100 text-teal-700' :
                                                                change.type === 'address_status' ? 'bg-yellow-100 text-yellow-700' :
                                                                change.type === 'address_contact' ? 'bg-emerald-100 text-emerald-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {change.before}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* اطلاعات کاربر و زمان */}
                                        <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2 mt-3">
                                            <div className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faUser} />
                                                <span>{log.employee?.name || 'نامشخص'}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faClock} />
                                                <span>{getDateAsReadableFormat(
                                                log.created_at,
                                                true
                                            )}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={`text-center py-8 transition-all duration-500 transform ${
                                isAnimating 
                                    ? 'translate-y-0 opacity-100' 
                                    : 'translate-y-4 opacity-0'
                            }`}>
                                <FontAwesomeIcon 
                                    icon={faFileInvoiceDollar} 
                                    className="text-gray-400 text-3xl mb-3"
                                />
                                <p className="text-gray-500">
                                    هیچ لاگی برای این مشتری ثبت نشده است
                                </p>
                            </div>
                        )}
                        
                        {/* کامپوننت صفحه‌بندی */}
                        {!isLoading && logs.length > 0 && pagination.last_page > 1 && (
                            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 mt-4">
                                <div className="flex items-center justify-between text-sm">
                                    {/* اطلاعات صفحه‌بندی */}
                                    <div className="text-gray-600">
                                        نمایش {pagination.from} تا {pagination.to} از {pagination.total} مورد
                                    </div>
                                    
                                    {/* دکمه‌های ناوبری */}
                                    <div className="flex items-center gap-2">
                                        {/* دکمه صفحه قبل */}
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page <= 1}
                                            className={`p-2 rounded-lg transition-colors ${
                                                pagination.current_page <= 1
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                                        </button>
                                        
                                        {/* شماره صفحات */}
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.last_page <= 5) {
                                                    pageNum = i + 1;
                                                } else {
                                                    if (pagination.current_page <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.current_page >= pagination.last_page - 2) {
                                                        pageNum = pagination.last_page - 4 + i;
                                                    } else {
                                                        pageNum = pagination.current_page - 2 + i;
                                                    }
                                                }
                                                
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handlePageChange(pageNum)}
                                                        className={`w-8 h-8 rounded-lg text-xs transition-colors ${
                                                            pageNum === pagination.current_page
                                                                ? 'bg-blue-500 text-white'
                                                                : 'text-gray-600 hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* دکمه صفحه بعد */}
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page >= pagination.last_page}
                                            className={`p-2 rounded-lg transition-colors ${
                                                pagination.current_page >= pagination.last_page
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LogCustomerSidebar; 